import { createElement, Key } from 'react'
import { ChromePicker } from 'react-color'
import {
  Form,
  Input,
  FormItemProps,
  InputProps,
  InputNumber,
  InputNumberProps,
  Slider,
  Upload,
  UploadProps,
  Button,
  ButtonProps,
  Select,
  SelectProps,
} from 'antd'

import { OptionProps } from 'antd/es/select'
import { SliderRangeProps, SliderSingleProps } from 'antd/es/slider'
import { TextAreaProps } from 'antd/es/input/TextArea'
type ExcludeChildren<T> = 'children' extends keyof T ? Omit<T, 'children'> : never

interface TextType {
  text: string
}

interface ButtonType {
  type: 'Button'
  props?: ExcludeChildren<ButtonProps> & TextType
}

interface ChromePickerType {
  type: 'ChromePicker'
  props?: {
    color?: string
  }
}

interface InputType {
  type: 'Input'
  props?: ExcludeChildren<InputProps>
}

interface InputNumberType {
  type: 'InputNumber'
  props?: ExcludeChildren<InputNumberProps>
  children?: null
}
interface TextareaType {
  type: 'Textarea'
  props?: TextAreaProps
  children?: null
}
interface SliderType {
  type: 'Slider'
  props?: SliderSingleProps | SliderRangeProps
}
interface UploadType {
  type: 'Upload'
  props?: UploadProps
  children?: ButtonType
}

interface OptionType {
  type: 'Option'
  props: OptionProps & TextType
}

interface SelectType {
  type: 'Select'
  props?: ExcludeChildren<SelectProps<any>>
  children?: OptionType[]
}

export type FormElementProps =
  | InputType
  | FormItemType
  | InputNumberType
  | SliderType
  | UploadType
  | ButtonType
  | ChromePickerType
  | SelectType
  | OptionType
  | TextareaType

interface FormItemType {
  type: 'FormItem'
  props?: ExcludeChildren<FormItemProps>
  children?: FormElementProps | JsonSchema
}

export type JsonSchema = FormElementProps[]

export const ComponentMaps = {
  Input,
  Password: Input.Password,
  FormItem: Form.Item,
  InputNumber,
  Slider,
  Upload,
  Button,
  ChromePicker,
  Select,
  Option: Select.Option,
  Textarea: Input.TextArea,
}

const isFormElementProps = (data: FormElementProps | unknown): data is FormElementProps => {
  return typeof data === 'object' && ComponentMaps[(data as FormElementProps)?.type] !== undefined
}

function renderElement(element: FormElementProps, key?: Key | null): any {
  switch (element.type) {
    case 'FormItem':
      return createElement(
        ComponentMaps[element.type],
        key ? { ...(element.props || {}), key } : element.props,
        renderSchema(element.children)
      )
    case 'Button':
      return createElement(
        ComponentMaps[element.type],
        key ? { ...(element.props || {}), key } : element.props,
        element.props?.text
      )
    case 'ChromePicker':
      return createElement(
        ComponentMaps[element.type],
        key ? { ...(element.props || {}), key } : element.props
      )
    case 'Input':
      return createElement(
        ComponentMaps[element.type],
        key ? { ...(element.props || {}), key } : element.props
      )
    case 'InputNumber':
      return createElement(
        ComponentMaps[element.type],
        key ? { ...(element.props || {}), key } : element.props
      )
    case 'Slider':
      return createElement(
        ComponentMaps[element.type] as any,
        (key ? { ...(element.props || {}), key } : element.props) as any
      )
    case 'Upload':
      return createElement(
        ComponentMaps[element.type],
        key ? { ...(element.props || {}), key } : element.props,
        renderSchema(element.children)
      )
    case 'Option':
      return createElement(
        ComponentMaps[element.type],
        key ? { ...(element.props || {}), key } : element.props,
        element.props.text
      )
    case 'Select':
      return createElement(
        ComponentMaps[element.type],
        key ? ({ ...(element.props || {}), key } as any) : element.props,
        renderSchema(element.children)
      )
    case 'Textarea':
      return createElement(
        ComponentMaps[element.type],
        key ? { ...(element.props || {}), key } : element.props
      )
    default:
      return null
  }
}

function renderSchema(data: JsonSchema | FormElementProps | undefined | null): any {
  if (data === undefined || data === null) {
    return null
  }
  if (Array.isArray(data)) {
    return data.map((form, index) =>
      isFormElementProps(form) ? renderElement(form, `${index}`) : form
    )
  } else if (isFormElementProps(data)) {
    return renderElement(data)
  } else {
    return data
  }
}

export default renderSchema
